import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Zap, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { money } from './shared';

export function StartPayout({ accId, stepToken, avail, instant, payoutsEnabled, onDone }) {
  const availAmount = avail?.amount || 0;
  const currency = avail?.currency || 'EUR';
  const [amount, setAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // Instant payout state
  const instantMax = instant?.amount || 0;
  const instantCur = instant?.currency || currency;
  const [instAmount, setInstAmount] = useState('');
  const [instConfirming, setInstConfirming] = useState(false);
  const [instBusy, setInstBusy] = useState(false);
  // Instant payout destination choice (RIB vs debit card)
  const [dests, setDests] = useState([]);
  const [destErr, setDestErr] = useState('');
  const [dest, setDest] = useState('auto');
  useEffect(() => { setInstAmount(instantMax > 0 ? String(instantMax) : ''); }, [instantMax]);

  useEffect(() => {
    if (instantMax <= 0) return;
    let alive = true;
    api.get(`/stripe-accounts/${accId}/payout-destinations`, { headers: { 'X-Step-Up': stepToken } })
      .then(({ data }) => {
        if (!alive) return;
        setDests(data.destinations || []);
        setDestErr(data.error || '');
      })
      .catch(() => { if (alive) setDestErr('Liste des destinations indisponible'); });
    return () => { alive = false; };
  }, [accId, stepToken, instantMax]);

  const destLabel = dest === 'auto'
    ? 'destination par défaut Stripe'
    : (dests.find((d) => d.id === dest)?.label || dest);

  useEffect(() => { setAmount(availAmount > 0 ? String(availAmount) : ''); }, [availAmount]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Montant invalide'); return; }
    setBusy(true);
    try {
      await api.post(`/stripe-accounts/${accId}/payouts`, { amount: amt, currency: currency.toLowerCase() }, { headers: { 'X-Step-Up': stepToken } });
      toast.success('Virement déclenché ✅');
      setConfirming(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Virement refusé par Stripe');
    } finally { setBusy(false); }
  };

  const submitInstant = async () => {
    const amt = Number(instAmount);
    if (!amt || amt <= 0) { toast.error('Montant invalide'); return; }
    setInstBusy(true);
    try {
      const { data } = await api.post(`/stripe-accounts/${accId}/instant-payout`, { amount: amt, currency: instantCur.toLowerCase(), destination: dest === 'auto' ? '' : dest }, { headers: { 'X-Step-Up': stepToken } });
      toast.success(`Virement instantané envoyé ⚡ (${money(data.amount, (data.currency || instantCur).toUpperCase())})`);
      setInstConfirming(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Virement instantané refusé par Stripe');
    } finally { setInstBusy(false); }
  };

  return (
    <Card className="card-flat p-6" data-testid="start-payout-card">
      <div className="flex items-center gap-2 text-zinc-700 font-semibold mb-1"><Send className="h-4 w-4" /> Démarrer un virement</div>
      <p className="text-sm text-zinc-500 mb-4">Vire tout ou partie du solde disponible vers le compte bancaire Stripe. Disponible : <b className="text-zinc-900">{money(availAmount, currency)}</b>.</p>
      {!payoutsEnabled && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">Les virements ne sont pas activés sur ce compte Stripe — la demande sera probablement refusée.</div>}
      {!confirming ? (
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Montant ({currency})</label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 w-40 rounded-md" data-testid="payout-amount-input" />
              <Button variant="outline" className="h-10 rounded-md text-xs" onClick={() => setAmount(String(availAmount))} disabled={availAmount <= 0} data-testid="payout-max-btn">Tout ({money(availAmount, currency)})</Button>
            </div>
          </div>
          <Button className="h-10 rounded-md bg-zinc-950" disabled={!amount || Number(amount) <= 0 || availAmount <= 0} onClick={() => setConfirming(true)} data-testid="payout-start-btn">
            <Send className="h-4 w-4 mr-1.5" /> Virer
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="payout-confirm">
          <p className="text-sm text-amber-900 font-medium mb-3">Confirmer le virement de <b>{money(Number(amount), currency)}</b> vers le compte bancaire ? Cette action déplace de l'argent réel.</p>
          <div className="flex gap-2">
            <Button className="h-9 rounded-md bg-amber-600 hover:bg-amber-700 text-white" onClick={submit} disabled={busy} data-testid="payout-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Oui, virer maintenant
            </Button>
            <Button variant="outline" className="h-9 rounded-md" onClick={() => setConfirming(false)} disabled={busy} data-testid="payout-cancel-btn">Annuler</Button>
          </div>
        </div>
      )}

      {/* Instant payout — only when the account is eligible */}
      {instantMax > 0 && (
        <div className="mt-5 pt-4 border-t border-zinc-100" data-testid="instant-payout-block">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-1">
            <Zap className="h-4 w-4" /> Virement instantané
          </div>
          <p className="text-sm text-zinc-500 mb-3">
            Reçu en ~30 minutes (24/7) sur la carte/RIB éligible. Limite max instantanée : <b className="text-emerald-700" data-testid="instant-max">{money(instantMax, instantCur)}</b>. <span className="text-zinc-400">Des frais Stripe s'appliquent.</span>
          </p>
          {!instConfirming ? (
            <div className="space-y-3">
              {/* Destination choice: Stripe default / RIB / debit card */}
              <div data-testid="instant-dest-picker">
                <label className="text-xs text-zinc-500 mb-1.5 block">Destination du virement instantané</label>
                <Select value={dest} onValueChange={setDest}>
                  <SelectTrigger className="h-10 w-full sm:w-[360px] rounded-md text-sm" data-testid="instant-dest-select">
                    <SelectValue placeholder="Choisir la destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto" data-testid="instant-dest-auto">⚡ Automatique — défaut Stripe</SelectItem>
                    {dests.map((d) => (
                      <SelectItem key={d.id} value={d.id} disabled={!d.instant_eligible} data-testid={`instant-dest-${d.id}`}>
                        {d.kind === 'card' ? '💳 ' : '🏦 '}{d.label}
                        {d.default ? ' · défaut' : ''}
                        {!d.instant_eligible ? ' · non éligible instantané' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dests.length === 0 && !destErr && (
                  <p className="text-[11px] text-zinc-400 mt-1">Aucune carte/RIB détecté pour l'instant — le virement partira vers la destination par défaut Stripe.</p>
                )}
                {destErr && <p className="text-[11px] text-zinc-400 mt-1">{destErr} — le virement partira vers la destination par défaut Stripe.</p>}
              </div>
              <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Montant ({instantCur})</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" step="0.01" value={instAmount} onChange={(e) => setInstAmount(e.target.value)} className="h-10 w-40 rounded-md" data-testid="instant-amount-input" />
                  <Button variant="outline" className="h-10 rounded-md text-xs" onClick={() => setInstAmount(String(instantMax))} data-testid="instant-max-btn">Max ({money(instantMax, instantCur)})</Button>
                </div>
              </div>
              <Button className="h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!instAmount || Number(instAmount) <= 0 || Number(instAmount) > instantMax} onClick={() => setInstConfirming(true)} data-testid="instant-start-btn">
                <Zap className="h-4 w-4 mr-1.5" /> Virer en instantané
              </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="instant-confirm">
              <p className="text-sm text-emerald-900 font-medium mb-3">Confirmer le virement <b>instantané</b> de <b>{money(Number(instAmount), instantCur)}</b> vers <b data-testid="instant-confirm-dest">{destLabel}</b> ? Réception en ~30 min, argent réel.</p>
              <div className="flex gap-2">
                <Button className="h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitInstant} disabled={instBusy} data-testid="instant-confirm-btn">
                  {instBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />} Oui, virer maintenant
                </Button>
                <Button variant="outline" className="h-9 rounded-md" onClick={() => setInstConfirming(false)} disabled={instBusy} data-testid="instant-cancel-btn">Annuler</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
